import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Eye, Users, Award, TrendingUp, Heart, ArrowRight, CheckCircle, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { TeamCarousel } from '../components/TeamCarousel';
import { teamMembers } from '../data/teamMembers';

const stats = [
  { value: '100+', label: 'Projekte im Partnernetzwerk', icon: Award },
  { value: '10M€+', label: 'Projektwert mit Partnern', icon: TrendingUp },
  { value: '70+', label: 'betreute Flächeneigentümer', icon: Heart },
  { value: '10+', label: 'Jahre gebündelte Erfahrung', icon: Calendar },
];

const milestones = [
  { year: 'Anfang 2023', title: 'Gründung', desc: 'Start als Unternehmen in der Akquise für Energieprojekte als Projektentwickler.' },
  { year: 'Ende 2023', title: 'Erstes Großprojekt', desc: '1,7 MWp Dach-PV-Projekt in Brandenburg.' },
  { year: '2024', title: 'Dach-PV-Projektentwicklung', desc: 'Ausbau der Projektentwicklung im Bereich Dach-Photovoltaik.' },
  { year: 'Anfang 2025', title: 'Partnerschaft mit Solarea', desc: 'Strategische Partnerschaft und Erweiterung des Portfolios auf große Batteriespeicher.' },
  { year: 'Mitte 2025', title: 'Partnerschaft mit ILOS Energy', desc: 'Erweiterung des Portfolios auf Freiflächen-Photovoltaik.' },
  { year: 'Ende 2025', title: 'Schwarmspeichersysteme', desc: 'Erweiterung des Portfolios um Schwarmspeichersysteme.' },
  { year: '2026', title: 'Heute', desc: 'Umbenennung in EN:POWER, Professionalisierung der Firmenstruktur und Projektpipeline mit mehr als 60 Projekten und über 150 MW.' },
];

const values = [
  { 
    title: 'Partnerschaftlich', 
    desc: 'Wir sehen uns als langfristiger Partner unserer Kunden. Ihr Erfolg ist unser Erfolg.',
    icon: Users
  },
  { 
    title: 'Transparent', 
    desc: 'Offene Kommunikation und klare Prozesse. Bei uns gibt es keine versteckten Kosten.',
    icon: Eye
  },
  { 
    title: 'Nachhaltig', 
    desc: 'Wir entwickeln Projekte, die wirtschaftlich und ökologisch Sinn machen.',
    icon: Heart
  },
  { 
    title: 'Zuverlässig', 
    desc: 'Was wir versprechen, halten wir. Termintreue und Qualität sind selbstverständlich.',
    icon: Award
  },
];

export const UeberUns = () => {
  return (
    <div className="min-h-screen" data-testid="ueber-uns-page">
      {/* Hero Section */}
      <section className="relative bg-[#122A5C] py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-96 h-96 bg-[#E07000] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#E07000] rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4">
              Über uns
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Die Energiewende braucht Macher.<br />
              <span className="text-[#E07000]">Wir sind dabei.</span>
            </h1>
            <p className="text-lg text-white/80 max-w-3xl mx-auto">
              EN:POWER steht für professionelle Projektentwicklung im Bereich erneuerbarer Energien. 
              Wir verbinden eigene Projektentwicklung mit der Erfahrung strategischer Partner und schaffen wirtschaftlich attraktive Modelle für Flächeneigentümer und Investoren.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <div className="w-14 h-14 bg-[#E07000]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-7 h-7 text-[#E07000]" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-[#122A5C] mb-1">{stat.value}</div>
                <div className="text-slate-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Vision */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-0 shadow-lg rounded-2xl overflow-hidden">
                <CardContent className="p-8 md:p-10">
                  <div className="w-14 h-14 bg-[#122A5C] rounded-xl flex items-center justify-center mb-6">
                    <Eye className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-[#122A5C] mb-4">
                    Unsere Vision
                  </h2>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    Eine Zukunft, in der erneuerbare Energien nicht nur umweltfreundlich, 
                    sondern auch wirtschaftlich die beste Wahl sind. Wir wollen dazu beitragen, 
                    dass jede geeignete Fläche in Deutschland zur Energiewende beiträgt.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Mission */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Card className="h-full border-0 shadow-lg rounded-2xl overflow-hidden bg-[#122A5C]">
                <CardContent className="p-8 md:p-10">
                  <div className="w-14 h-14 bg-[#E07000] rounded-xl flex items-center justify-center mb-6">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="font-heading text-2xl font-bold text-white mb-4">
                    Unsere Mission
                  </h2>
                  <p className="text-lg text-white/80 leading-relaxed">
                    Wir entwickeln maßgeschneiderte Energieprojekte, die für alle Beteiligten 
                    Mehrwert schaffen. Flächeneigentümer profitieren von sicheren Einnahmen, 
                    Investoren von attraktiven Renditen und die Gesellschaft von sauberer Energie.
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4 block">
              Wofür wir stehen
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#122A5C]">
              Unsere Werte
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border border-slate-200 hover:border-[#E07000] rounded-2xl card-hover">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 bg-[#E07000]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <value.icon className="w-6 h-6 text-[#E07000]" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-[#122A5C] mb-2">
                      {value.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {value.desc}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4 block">
              Unser Team
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#122A5C] mb-4">
              Die Menschen hinter EN:POWER
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Erfahrene Experten mit Leidenschaft für erneuerbare Energien. 
              Persönlich, kompetent und immer an Ihrer Seite.
            </p>
          </motion.div>

          <TeamCarousel members={teamMembers} />
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#E07000] mb-4 block">
              Unsere Geschichte
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#122A5C]">
              Meilensteine
            </h2>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-gradient-to-b from-[#E07000] to-[#122A5C]" />

            {milestones.map((milestone, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`relative flex items-center mb-8 ${
                  index % 2 === 0 ? 'justify-start' : 'justify-end'
                }`}
              >
                <div className={`w-5/12 ${index % 2 === 0 ? 'text-right pr-8' : 'text-left pl-8 order-2'}`}>
                  <div className="text-2xl font-bold text-[#E07000] mb-1">{milestone.year}</div>
                  <div className="font-semibold text-[#122A5C] mb-1">{milestone.title}</div>
                  <div className="text-sm text-slate-600">{milestone.desc}</div>
                </div>
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-[#E07000] rounded-full border-4 border-white shadow" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#122A5C]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Werden Sie Teil unserer Erfolgsgeschichte
            </h2>
            <p className="text-xl text-white/80 mb-8">
              Lassen Sie uns gemeinsam Ihr Projekt realisieren.
            </p>
            <Link to="/angebot-anfordern">
              <Button 
                className="bg-[#E07000] hover:bg-[#C56200] text-white rounded-full px-8 py-4 text-lg font-semibold btn-animate"
                data-testid="ueber-uns-cta"
              >
                Kontakt aufnehmen
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
